import { type HTMLAttributes, type ReactNode } from 'react';

import { css, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { EASE_OUT, staggerDelay } from '../jsm-playground-motion';

/**
 * The table parts both survey tables share: an outlined frame, a sunken header row, ruled columns,
 * and rows that fade up one after the next when the table lands.
 */
export const SurveyTable = ({ caption, children }: { caption: string; children: ReactNode }) => (
	<div css={frameStyles}>
		<table css={tableStyles}>
			<caption>
				<VisuallyHidden>{caption}</VisuallyHidden>
			</caption>
			{children}
		</table>
	</div>
);

export const SurveyTableHead = ({ children }: { children: ReactNode }) => (
	<thead css={sectionResetStyles}>
		<tr>{children}</tr>
	</thead>
);

export const SurveyTableBody = ({ children }: { children: ReactNode }) => (
	<tbody css={sectionResetStyles}>{children}</tbody>
);

export const SurveyHeadCell = ({
	width,
	isFirst = false,
	children,
}: {
	/** In px. Omit for the column that takes the remaining width. */
	width?: number;
	isFirst?: boolean;
	children: ReactNode;
}) => (
	<th
		scope="col"
		css={[cellStyles, headCellStyles, !isFirst && dividerStyles]}
		style={width === undefined ? undefined : { width: `${width}px` }}
	>
		{children}
	</th>
);

export const SurveyCell = ({
	isFirst = false,
	children,
}: {
	isFirst?: boolean;
	children: ReactNode;
}) => <td css={[cellStyles, !isFirst && dividerStyles]}>{children}</td>;

export const SurveyTableRow = ({
	index,
	isSelected = false,
	isActionRevealed = false,
	children,
	...rest
}: {
	/** Position in the table, which staggers its entrance behind the row above. */
	index: number;
	/** Keeps the selected fill, and keeps the row's action showing. */
	isSelected?: boolean;
	/** Shows the row's action as `:hover` would, for a scripted cursor that never fires it. */
	isActionRevealed?: boolean;
	children: ReactNode;
} & Omit<HTMLAttributes<HTMLTableRowElement>, 'style'>) => (
	<tr
		css={[rowStyles, isActionRevealed && revealedRowStyles, isSelected && selectedRowStyles]}
		style={{ animationDelay: staggerDelay(index, 50) }}
		{...rest}
	>
		{children}
	</tr>
);

/**
 * A row's trailing action, shown while the row is hovered, holds focus, or is selected. It stays
 * mounted while hidden so the row doesn't reflow the moment the pointer lands on it.
 */
export const SurveyRowAction = ({ children }: { children: ReactNode }) => (
	<span css={rowActionStyles}>{children}</span>
);

// `--survey-row-action-opacity` is set by the row and read by its action, which lets the row reveal
// the action without a nested selector.
const fadeUp = keyframes({
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'none' },
});

const frameStyles = css({
	boxSizing: 'border-box',
	width: '100%',
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
	overflow: 'hidden',
});

const tableStyles = css({
	width: '100%',
	borderCollapse: 'collapse',
	borderSpacing: 0,
});

// The global CSS reset rules a 2px line under `thead` and `tbody`, which would stack on the rows'
// own 1px rules.
const sectionResetStyles = css({
	borderBlockEnd: 'none',
});

const cellStyles = css({
	boxSizing: 'border-box',
	paddingBlock: token('space.150'),
	paddingInline: token('space.150'),
	color: token('color.text'),
	font: token('font.body'),
	textAlign: 'start',
	verticalAlign: 'middle',
});

const headCellStyles = css({
	borderBlockEnd: `${token('border.width', '1px')} solid ${token('color.border')}`,
	backgroundColor: token('elevation.surface.sunken'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
});

const dividerStyles = css({
	borderInlineStart: `${token('border.width', '1px')} solid ${token('color.border')}`,
});

// The hover fill fades on its own short timing, separate from the staggered entrance, so a hover
// never inherits a row's entrance delay.
const rowStyles = css({
	borderBlockEnd: `${token('border.width', '1px')} solid ${token('color.border')}`,
	animation: `${fadeUp} 260ms ${EASE_OUT} both`,
	transition: `background-color 100ms ${EASE_OUT}`,
	'&:last-of-type': {
		borderBlockEnd: 'none',
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:hover, &:focus-within': {
		'--survey-row-action-opacity': '1',
	},
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const revealedRowStyles = css({
	backgroundColor: token('color.background.neutral.subtle.hovered'),
	'--survey-row-action-opacity': '1',
});

const selectedRowStyles = css({
	backgroundColor: token('color.background.selected'),
	'--survey-row-action-opacity': '1',
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const rowActionStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	opacity: 'var(--survey-row-action-opacity, 0)',
	transition: `opacity 100ms ${EASE_OUT}`,
});
